import { Box, TextField } from "@mui/material";
import { useRef, useState, useEffect } from "react";

interface ISecurityDigit {
    value: number | undefined;
    error: boolean;
    inputRef: React.RefObject<HTMLInputElement>;
}

interface VerificationCodeInputProps {
    value?: string;
    onChange?: (value: string) => void;
    error?: boolean;
    dataTestIdPrefix?: string;
}

export function VerificationCodeInput({
    value = "",
    onChange,
    error = false,
    dataTestIdPrefix,
}: VerificationCodeInputProps) {
    const createEmptySecurityCodes = (): ISecurityDigit[] =>
        Array.from({ length: NUMBER_OF_DIGITS }).map(() => ({
            value: undefined,
            error: false,
            // eslint-disable-next-line react-hooks/rules-of-hooks
            inputRef: useRef<HTMLInputElement>(null),
        }));
    const [securityCodeDigits, setSecurityCodeDigits] = useState<ISecurityDigit[]>(createEmptySecurityCodes());

    // Sync external value to internal state
    useEffect(() => {
        if (value) {
            const digits = value.split("").slice(0, NUMBER_OF_DIGITS);
            setSecurityCodeDigits((prev) =>
                prev.map((item, index) => ({
                    ...item,
                    value: digits[index] ? Number(digits[index]) : undefined,
                    error: error,
                }))
            );
        } else {
            setSecurityCodeDigits((prev) =>
                prev.map((item) => ({
                    ...item,
                    value: undefined,
                    error: error,
                }))
            );
        }
    }, [value, error]);

    function setNewDigitValue(index: number, newValue: number | undefined) {
        setSecurityCodeDigits((securityDigits) => {
            const updated = securityDigits.map((item, i) =>
                i === index ? { ...item, value: newValue, error: newValue === undefined && error } : item
            );

            // Notify parent of change
            if (onChange) {
                const code = updated.map((item) => (item.value !== undefined ? item.value : "")).join("");
                onChange(code);
            }

            return updated;
        });
    }

    function focusNextInput(index: number) {
        if (index < securityCodeDigits.length - 1) {
            securityCodeDigits[index + 1]?.inputRef.current?.focus();
        }
    }

    function focusPreviousInput(index: number) {
        if (index > 0) {
            securityCodeDigits[index - 1]?.inputRef.current?.focus();
        }
    }

    function getInputValue(value: string): number | undefined {
        const newValue = value.replace(/[^0-9]/g, "").substring(value.length - 1);
        return newValue ? Number(newValue) : undefined;
    }

    function handleDigitChange(index: number, value: string) {
        const newValue = getInputValue(value);
        setNewDigitValue(index, newValue);

        // Set focus to the next input field if available
        if (newValue !== undefined) {
            focusNextInput(index);
        }
    }

    function handleOnChange(index: number, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        handleDigitChange(index, e.target.value);
    }

    function handleOneInputKeyDown(index: number, e: React.KeyboardEvent<HTMLDivElement>) {
        if (KEYBOARD_KEY.backspace === e.key) {
            e.preventDefault();
            setNewDigitValue(index, undefined);
            focusPreviousInput(index);
        } else if (KEYBOARD_KEY.left === e.key) {
            e.preventDefault();
            focusPreviousInput(index);
        } else if (KEYBOARD_KEY.right === e.key) {
            e.preventDefault();
            focusNextInput(index);
        }
    }

    function handleOneInputPaste(e: React.ClipboardEvent<HTMLInputElement>) {
        e.preventDefault();
        const pastedText = e.clipboardData.getData("text/plain");
        const digits = pastedText.split("").slice(0, NUMBER_OF_DIGITS);

        const updatedDigits = [...securityCodeDigits];
        digits.forEach((value, index) => {
            const numValue = getInputValue(value);
            if (numValue !== undefined && index < NUMBER_OF_DIGITS) {
                updatedDigits[index] = {
                    ...updatedDigits[index],
                    value: numValue,
                    error: false,
                };
            }
        });

        setSecurityCodeDigits(updatedDigits);

        // Notify parent of change
        if (onChange) {
            const code = updatedDigits.map((item) => (item.value !== undefined ? item.value : "")).join("");
            onChange(code);
        }

        // Focus the next empty input or the last one
        const nextEmptyIndex = updatedDigits.findIndex((d) => d.value === undefined);
        if (nextEmptyIndex !== -1) {
            updatedDigits[nextEmptyIndex]?.inputRef.current?.focus();
        } else if (digits.length < NUMBER_OF_DIGITS) {
            updatedDigits[digits.length]?.inputRef.current?.focus();
        }
    }

    return (
        <Box display="flex" gap="8px" marginTop="4px" marginBottom="4px">
            {securityCodeDigits.map((securityDigit, index) => (
                <TextField
                    key={index}
                    sx={{
                        input: { textAlign: "center", height: 31 },
                        width: 48,
                    }}
                    inputRef={securityDigit.inputRef}
                    placeholder="-"
                    type="tel"
                    value={securityDigit.value ?? ""}
                    error={securityDigit.error || error}
                    onChange={(e) => handleOnChange(index, e)}
                    onKeyDown={(e) => handleOneInputKeyDown(index, e)}
                    onPaste={handleOneInputPaste}
                    data-test-id={dataTestIdPrefix ? `${dataTestIdPrefix}_digit_${index}` : undefined}
                    slotProps={{
                        htmlInput: {
                            "aria-label": `Verification code digit ${index + 1}`,
                            maxLength: 1,
                        },
                    }}
                />
            ))}
        </Box>
    );
}

const KEYBOARD_KEY = {
    left: "ArrowLeft",
    right: "ArrowRight",
    backspace: "Backspace",
};

const NUMBER_OF_DIGITS = 6;
